(function () {
  'use strict';

  const sliderInstances = {};
  let resizeTimeout = null;

  const REPEAT = 20;
  const MOBILE_BREAKPOINT = 1130;

  /* =====================================================
     INIT
  ===================================================== */
  function initInfiniteCenteredSlider(containerSelector = '[data-slider-container]') {
    destroySlider(containerSelector);

    const container = document.querySelector(containerSelector);
    if (!container) return;

    // Extract unique ID from container selector (e.g., '-500' from '[data-slider-container-500]')
    const uniqueIdMatch = containerSelector.match(/\[data-slider-container(-[^\]]+)?\]/);
    const uniqueId = uniqueIdMatch && uniqueIdMatch[1] ? uniqueIdMatch[1] : '';
    
    const listSelector = `[data-slider-list${uniqueId}]`;
    const list = container.querySelector(listSelector);
    if (!list) return;

    const prevBtnSelector = `[data-nav-prev${uniqueId}]`;
    const nextBtnSelector = `[data-nav-next${uniqueId}]`;
    
    const section = container.closest('section');
    const prevBtns = section ? Array.from(section.querySelectorAll(prevBtnSelector)) : [];
    const nextBtns = section ? Array.from(section.querySelectorAll(nextBtnSelector)) : [];

    const originals = Array.from(list.children);
    if (originals.length < 2) return;

    const isDesktop = window.matchMedia(`(min-width: ${MOBILE_BREAKPOINT + 1}px)`).matches;

    /* =====================================================
       BUILD LONG STRIP (COMMON FOR MOBILE + DESKTOP)
    ===================================================== */
    const fragment = document.createDocumentFragment();
    for (let i = 0; i < REPEAT; i++) {
      originals.forEach(item => fragment.appendChild(item.cloneNode(true)));
    }

    list.innerHTML = '';
    list.appendChild(fragment);

    const items = Array.from(list.children);

    let index = originals.length * Math.floor(REPEAT / 2);
    let step = 0;
    let containerCenterOffset = 0;
    let isAnimating = false;

    const VISUAL_OFFSET = isDesktop ? 130 : 0;
    const DURATION = isDesktop ? 0.45 : 0.35;

    /* =====================================================
       CALCULATIONS
    ===================================================== */
    function calculateStep() {
      const item = items[0];
      if (!item) return 0;

      const styles = getComputedStyle(list);
      const gap = parseFloat(styles.gap) || 0;

      const itemWidth = item.offsetWidth;
      const containerWidth = container.offsetWidth;

      containerCenterOffset = isDesktop
        ? (containerWidth / 2) - (itemWidth / 2)
        : 0;

      return itemWidth + gap;
    }

    function apply(noTransition = false) {
      list.style.transition = noTransition
        ? 'none'
        : `transform ${DURATION}s cubic-bezier(0.4, 0, 0.2, 1)`;

      list.style.transform =
        `translateX(${containerCenterOffset - index * step - VISUAL_OFFSET}px)`;
    }

    /* =====================================================
       SCALE (DESKTOP ONLY)
    ===================================================== */
    function updateScale() {
      if (!isDesktop) return;

      items.forEach((item, i) => {
        const distance = Math.abs(i - index);
        const scale = Math.max(0.85, 1 - distance * 0.08);
        item.style.transform = `scale(${scale})`;
        item.classList.toggle('is-centered', i === index);
      });
    }

    /* =====================================================
       MOVE
    ===================================================== */
    function move(direction) {
      if (isAnimating) return;
      isAnimating = true;

      index += direction === 'next' ? 1 : -1;
      apply();
      updateScale();
    }

    function onTransitionEnd() {
      const min = originals.length * 2;
      const max = originals.length * (REPEAT - 2);

      if (index < min || index > max) {
        index = originals.length * Math.floor(REPEAT / 2);
        apply(true);
        updateScale();
      }

      isAnimating = false;
    }

    list.addEventListener('transitionend', onTransitionEnd);

    /* =====================================================
       NAV
    ===================================================== */
    prevBtns.forEach(btn => {
      btn.onclick = e => {
        e.preventDefault();
        move('prev');
        restartAutoplay();
      };
    });

    nextBtns.forEach(btn => {
      btn.onclick = e => {
        e.preventDefault();
        move('next');
        restartAutoplay();
      };
    });

    /* =====================================================
       AUTOPLAY (every 3s)
    ===================================================== */
    const AUTOPLAY_MS = 3000;
    let autoplayTimer = null;

    function stopAutoplay() {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    }

    function startAutoplay() {
      stopAutoplay();
      autoplayTimer = setInterval(() => {
        move('next');
      }, AUTOPLAY_MS);
    }

    function restartAutoplay() {
      startAutoplay();
    }

    container.addEventListener('mouseenter', stopAutoplay);
    container.addEventListener('mouseleave', startAutoplay);
    container.addEventListener('touchstart', stopAutoplay, { passive: true });
    container.addEventListener('touchend', startAutoplay, { passive: true });

    /* =====================================================
       ENSURE LINKS WORK AFTER CLONING
    ===================================================== */
    // Ensure all product links work correctly after cloning
    items.forEach(item => {
      const link = item.querySelector('a[href]');
      if (link) {
        // Make sure link is clickable and not blocked
        link.style.pointerEvents = 'auto';
        link.style.cursor = 'pointer';
        // Remove any event listeners that might block navigation
        link.onclick = null;
      }
    });

    /* =====================================================
       INIT APPLY
    ===================================================== */
    function init() {
      step = calculateStep();
      apply(true);
      updateScale();
      startAutoplay();
    }

    init();

    sliderInstances[containerSelector] = {
      list,
      originals,
      onTransitionEnd,
      stopAutoplay,
      startAutoplay,
      container
    };
  }

  /* =====================================================
     DESTROY
  ===================================================== */
  function destroySlider(containerSelector = '[data-slider-container]') {
    const sliderInstance = sliderInstances[containerSelector];
    if (!sliderInstance) return;

    if (typeof sliderInstance.stopAutoplay === 'function') {
      sliderInstance.stopAutoplay();
    }

    const container = sliderInstance.container || document.querySelector(containerSelector);
    if (container && typeof sliderInstance.stopAutoplay === 'function') {
      container.removeEventListener('mouseenter', sliderInstance.stopAutoplay);
      container.removeEventListener('mouseleave', sliderInstance.startAutoplay);
      container.removeEventListener('touchstart', sliderInstance.stopAutoplay);
      container.removeEventListener('touchend', sliderInstance.startAutoplay);
    }

    if (!container) return;

    // Extract unique ID from container selector
    const uniqueIdMatch = containerSelector.match(/\[data-slider-container(-[^\]]+)?\]/);
    const uniqueId = uniqueIdMatch && uniqueIdMatch[1] ? uniqueIdMatch[1] : '';
    
    const listSelector = `[data-slider-list${uniqueId}]`;
    const list = container.querySelector(listSelector);
    if (!list) return;

    list.removeEventListener('transitionend', sliderInstance.onTransitionEnd);
    list.style.transform = '';
    list.style.transition = '';
    list.innerHTML = '';

    sliderInstance.originals.forEach(item => list.appendChild(item));

    delete sliderInstances[containerSelector];
  }

  /* =====================================================
     EVENTS
  ===================================================== */
  function initAllSliders() {
    // Find all slider containers on the page (with any unique ID suffix)
    // Use attribute selector to find all elements with data-slider-container (with or without suffix)
    const containers = document.querySelectorAll('[data-slider-container], [data-slider-container-500], [data-slider-container-403]');
    const processedSelectors = new Set();
    
    containers.forEach(container => {
      // Get the full attribute name (e.g., 'data-slider-container-500' or 'data-slider-container')
      const attributes = Array.from(container.attributes);
      const sliderAttr = attributes.find(attr => attr.name.startsWith('data-slider-container'));
      
      if (sliderAttr) {
        const selector = `[${sliderAttr.name}]`;
        if (!processedSelectors.has(selector)) {
          processedSelectors.add(selector);
          initInfiniteCenteredSlider(selector);
        }
      }
    });
  }

  document.addEventListener('DOMContentLoaded', initAllSliders);
  document.addEventListener('shopify:section:load', initAllSliders);
  document.addEventListener('shopify:section:unload', () => {
    // Destroy all sliders
    Object.keys(sliderInstances).forEach(selector => {
      destroySlider(selector);
    });
  });

  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(initAllSliders, 200);
  });

})();

